import { useState, useEffect } from 'react';
import { contactsAPI } from '../../lib/api';
import { LOCATION_ENRICHMENT_DISCLOSURE } from '../../lib/enrichmentDisclosure';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import {
  Trash2, Check, AlertTriangle, GitMerge, Loader2, SkipForward,
  SearchCheck, Sparkles, CheckCircle,
} from 'lucide-react';

/* ── Virtualized Contact List for Manage Tab ── */
export function VirtualizedContactList({ contacts, selected, toggleOne, parentRef }) {
  const virtualizer = useVirtualizer({
    count: contacts.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 48,
    overscan: 10,
  });

  return (
    <div style={{ height: virtualizer.getTotalSize(), width: '100%', position: 'relative' }}>
      {virtualizer.getVirtualItems().map((virtualRow) => {
        const c = contacts[virtualRow.index];
        return (
          <div
            key={c.id}
            ref={virtualizer.measureElement}
            data-index={virtualRow.index}
            role="button"
            tabIndex={0}
            onClick={() => toggleOne(c.id)}
            className={`absolute left-0 w-full flex items-center gap-3 px-3 py-2 text-left transition-colors cursor-pointer ${selected.has(c.id) ? 'bg-nav-blue/5' : 'hover:bg-secondary'}`}
            style={{ top: virtualRow.start }}
            data-testid={`manage-contact-${c.id}`}
          >
            <Checkbox checked={selected.has(c.id)} className="pointer-events-none" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{c.first_name} {c.last_name}</p>
              <p className="text-[10px] text-muted-foreground truncate">
                {c.emails?.[0]?.email || c.phones?.[0]?.number || c.addresses?.[0]?.city || ''}
              </p>
            </div>
            {c.source && c.source !== 'manual' && (
              <Badge variant="outline" className="text-[8px] flex-shrink-0">{c.source}</Badge>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ── Compare Dialog ── */
export function CompareDialog({ open, onClose, contacts, onMerge, onDelete, merging, deleting }) {
  const [keepId, setKeepId] = useState(null);
  const [review, setReview] = useState(null);
  const [reviewError, setReviewError] = useState('');
  const selectionKey = JSON.stringify(contacts.map(c => c.id));
  useEffect(() => {
    let active = true;
    setReview(null);
    setReviewError('');
    if (open && JSON.parse(selectionKey).length >= 2) {
      contactsAPI.connectedMergePreview(JSON.parse(selectionKey)).then(({ data }) => {
        if (active) {
          setReview(data);
          if (data.connected) setKeepId(data.keep_id);
        }
      }).catch(() => {
        if (active) setReviewError('Could not prepare this merge. Select one connected contact and its duplicate(s), then reopen Compare.');
      });
    }
    return () => { active = false; };
  }, [open, selectionKey]);

  useEffect(() => {
    if (contacts.length > 0) setKeepId(contacts[0].id);
  }, [contacts]);

  if (!open || contacts.length < 2) return null;
  const chosenId = review?.connected ? review.keep_id : keepId;

  const fields = [
    { key: 'first_name', label: 'First Name' },
    { key: 'last_name', label: 'Last Name' },
    { key: 'phones', label: 'Phones', format: (v) => (v || []).map(p => p.number).join(', ') || '-' },
    { key: 'emails', label: 'Emails', format: (v) => (v || []).map(e => e.email).join(', ') || '-' },
    { key: 'addresses', label: 'Addresses', format: (v) => (v || []).map(a => [a.street, a.city, a.state].filter(Boolean).join(', ')).join(' | ') || '-' },
    { key: 'work', label: 'Work', format: (v) => v ? [v.title, v.company].filter(Boolean).join(' at ') : '-' },
    { key: 'school', label: 'School', format: (v) => v || '-' },
    { key: 'notes', label: 'Notes', format: (v) => v ? (v.length > 60 ? v.slice(0, 60) + '...' : v) : '-' },
    { key: 'birthday', label: 'Birthday', format: (v) => v || '-' },
    { key: 'source', label: 'Source', format: (v) => v || '-' },
  ];

  const getValue = (contact, field) => {
    const raw = contact[field.key];
    return field.format ? field.format(raw) : (raw || '-');
  };

  const handleMerge = () => {
    if (!keepId || !review) return;
    if (review.connected) {
      onMerge(review.keep_id, {}, review.token);
      return;
    }
    const keep = contacts.find(c => c.id === keepId);
    if (!keep) return;

    const merged = {};
    const allPhones = [];
    const allEmails = [];
    const allAddresses = [];
    const seenPhones = new Set();
    const seenEmails = new Set();

    for (const c of contacts) {
      for (const p of (c.phones || [])) {
        const norm = p.number.replace(/\D/g, '').slice(-7);
        if (!seenPhones.has(norm)) { seenPhones.add(norm); allPhones.push(p); }
      }
      for (const e of (c.emails || [])) {
        const norm = e.email.toLowerCase();
        if (!seenEmails.has(norm)) { seenEmails.add(norm); allEmails.push(e); }
      }
      for (const a of (c.addresses || [])) {
        allAddresses.push(a);
      }
    }

    merged.phones = allPhones;
    merged.emails = allEmails;
    merged.addresses = allAddresses;

    for (const f of ['work', 'school', 'notes', 'birthday', 'image_url', 'nickname']) {
      if (keep[f]) {
        merged[f] = keep[f];
      } else {
        for (const c of contacts) {
          if (c[f]) { merged[f] = c[f]; break; }
        }
      }
    }

    onMerge(keepId, merged);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg">Compare {contacts.length} contacts</DialogTitle>
        </DialogHeader>
        <div className="overflow-x-auto" data-testid="compare-dialog">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-2 px-2 text-xs text-muted-foreground font-medium w-24">Field</th>
                {contacts.map(c => (
                  <th key={c.id} className="text-left py-2 px-2">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => !review?.connected && setKeepId(c.id)}
                        className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium transition-colors ${
                          chosenId === c.id
                            ? 'bg-green-500 text-white'
                            : 'bg-secondary text-muted-foreground hover:bg-secondary/80'
                        }`}
                        data-testid={`compare-keep-${c.id}`}
                      >
                        {chosenId === c.id && <Check size={12} />}
                        {c.first_name} {c.last_name || ''}
                      </button>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {fields.map(f => {
                const values = contacts.map(c => getValue(c, f));
                const allSame = values.every(v => v === values[0]);
                return (
                  <tr key={f.key} className={`border-b border-border/30 ${!allSame ? 'bg-amber-50 dark:bg-amber-950/20' : ''}`}>
                    <td className="py-2 px-2 text-xs font-medium text-muted-foreground whitespace-nowrap">{f.label}</td>
                    {contacts.map((c, i) => (
                      <td key={c.id} className="py-2 px-2 text-xs text-foreground max-w-[200px] truncate" title={values[i]}>
                        {values[i]}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {review?.connected && (
          <div data-testid="connected-merge-review" className="space-y-3 text-sm">
            <p>The connected contact stays. Its filled fields win; duplicates fill blanks. Notes are added together. Duplicate phone and email values are not added when the connected contact already has that field.</p>
            <p>{review.photo_note}</p>
            {review.rows.map(row => (
              <div key={row.field} className="border-t pt-2 whitespace-pre-wrap break-words">
                <strong>{row.field}</strong>
                <p>Connected: {row.connected || '(blank)'}</p>
                <p>Duplicate: {row.duplicate || '(blank)'}</p>
                <p>After merge: {row.result || '(blank)'}</p>
              </div>
            ))}
            <p>Conflicting duplicate values absent from the result leave the active card. The original cards remain recoverable in merge history. Apple changes still require Review &amp; Send.</p>
          </div>
        )}
        {!review && <p role="status">{reviewError || 'Preparing merge review...'}</p>}
        <div className="flex items-center justify-between pt-3 border-t border-border">
          <p className="text-xs text-muted-foreground">
            {review?.connected ? 'The connected contact is kept automatically. Approve only after reviewing the result.' : 'Click a name to select which contact to keep. Highlighted rows show differences.'}
          </p>
          <div className="flex gap-2">
            {onDelete ? (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => onDelete(contacts)}
                disabled={deleting || contacts.length < 1}
                className="rounded-full text-xs"
                data-testid="compare-delete-btn"
              >
                {deleting ? 'Deleting...' : `Delete ${contacts.length}`}
              </Button>
            ) : null}
            <Button variant="outline" size="sm" onClick={onClose} className="rounded-full text-xs" data-testid="compare-cancel-btn">Cancel</Button>
            <Button size="sm" onClick={handleMerge} disabled={!chosenId || merging || !review} className="rounded-full text-xs bg-sky-500 hover:bg-sky-400 text-white" data-testid="compare-merge-btn">
              <GitMerge size={13} className="mr-1" /> {merging ? 'Merging...' : `Merge into ${contacts.find(c => c.id === chosenId)?.first_name || ''}`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ── Duplicate Scan Settings Modal ── */
export function DuplicateScanModal({ open, onClose, onResults }) {
  const [threshold, setThreshold] = useState(0.99);
  const [scanning, setScanning] = useState(false);

  const thresholdOptions = [
    { value: 0.99, label: '99% (safest)' },
    { value: 0.95, label: '95%' },
    { value: 0.90, label: '90%' },
    { value: 0.80, label: '80% (aggressive)' },
  ];

  const descriptions = {
    0.99: {
      auto: 'Contacts with match confidence \u2265 99% will be auto-merged silently.',
      review: 'Contacts with match confidence between 80% and 99% will require your review.',
    },
    0.95: {
      auto: 'Contacts with match confidence \u2265 95% will be auto-merged silently.',
      review: 'Contacts with match confidence between 80% and 95% will require your review.',
    },
    0.90: {
      auto: 'Contacts with match confidence \u2265 90% will be auto-merged silently.',
      review: 'Contacts with match confidence between 80% and 90% will require your review.',
    },
    0.80: {
      auto: 'Contacts with match confidence \u2265 80% will be auto-merged silently.',
      review: 'No additional review — all matches \u2265 80% will be auto-merged.',
    },
  };

  const desc = descriptions[threshold];

  const runScan = async () => {
    setScanning(true);
    try {
      const { data } = await contactsAPI.scanDuplicates(threshold);
      onResults(data);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Duplicate scan failed. Please try again.';
      toast.error(msg);
    }
    setScanning(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="dup-scan-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg">Find and Merge Duplicate Contacts</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground leading-relaxed">
          We&apos;ll scan all your contacts for possible duplicates. You can choose how aggressive the automatic merging should be, and when to ask for your approval.
        </p>

        <div className="space-y-3 pt-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Auto-merge settings</p>

          <div className="space-y-1.5">
            <p className="text-sm font-medium">Auto-merge contacts when confidence is at least:</p>
            <div className="flex flex-col gap-1.5">
              {thresholdOptions.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setThreshold(opt.value)}
                  className={`flex items-center gap-2.5 px-3 py-2 rounded-lg border text-sm text-left transition-colors ${
                    threshold === opt.value
                      ? 'border-nav-blue bg-nav-blue/5 text-foreground font-medium'
                      : 'border-border hover:bg-secondary text-muted-foreground'
                  }`}
                  data-testid={`threshold-${opt.value * 100}`}
                >
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                    threshold === opt.value ? 'border-nav-blue' : 'border-muted-foreground/40'
                  }`}>
                    {threshold === opt.value && <div className="w-2 h-2 rounded-full bg-nav-blue" />}
                  </div>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-1 bg-secondary/50 rounded-lg p-3">
            <p className="text-xs text-foreground">{desc.auto}</p>
            <p className="text-xs text-foreground">{desc.review}</p>
            <Separator className="my-1.5" />
            <p className="text-xs text-muted-foreground">Contacts with match confidence &lt; 75% will not be merged. They can be reviewed later in an advanced view.</p>
          </div>
        </div>

        <div className="flex gap-2 pt-2 justify-end">
          <Button variant="outline" size="sm" onClick={onClose} className="rounded-full text-xs" data-testid="dup-scan-cancel-btn">Cancel</Button>
          <Button size="sm" onClick={runScan} disabled={scanning} className="rounded-full text-xs bg-nav-blue hover:bg-nav-blue/80 text-white" data-testid="dup-scan-run-btn">
            {scanning ? <><Loader2 size={13} className="mr-1 animate-spin" /> Scanning...</> : <><SearchCheck size={13} className="mr-1" /> Run Duplicate Scan</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ── Duplicate Results Modal ── */
export function DuplicateResultsModal({ open, results, onClose, onChanged }) {
  const [reviewPairs, setReviewPairs] = useState([]);
  const [merging, setMerging] = useState(null);
  const [showAutoMerged, setShowAutoMerged] = useState(false);
  const [reverting, setReverting] = useState(null);

  useEffect(() => {
    if (results) {
      setReviewPairs((results.review_pairs || []).map((p, i) => ({ ...p, _idx: i, status: 'pending' })));
      setShowAutoMerged(false);
    }
  }, [results]);

  const mergePair = async (idx) => {
    const pair = reviewPairs.find(p => p._idx === idx);
    if (!pair) return;
    setMerging(idx);
    try {
      // Server-side merge: folds contact_b into contact_a (deduped phones/
      // emails, unioned addresses/socials/groups, survivor wins) and archives
      // it in one call, with revert history recorded.
      await contactsAPI.mergeWithHistory(
        pair.contact_a.id,
        [pair.contact_b.id],
        'review_scan',
        pair.confidence
      );
      setReviewPairs(prev => prev.map(p => p._idx === idx ? { ...p, status: 'merged' } : p));
      toast.success(`Merged "${pair.contact_b.first_name}" into "${pair.contact_a.first_name}"`);
      if (onChanged) onChanged();
    } catch {
      toast.error('Merge failed');
    }
    setMerging(null);
  };

  const skipPair = (idx) => {
    setReviewPairs(prev => prev.map(p => p._idx === idx ? { ...p, status: 'skipped' } : p));
  };

  const revertAutoMerge = async (detail) => {
    setReverting(detail.merge_id);
    try {
      await contactsAPI.revertMerge(detail.merge_id);
      if (results?.auto_merged_details) {
        const updated = results.auto_merged_details.map(d =>
          d.merge_id === detail.merge_id ? { ...d, reverted: true } : d
        );
        results.auto_merged_details = updated;
      }
      toast.success('Merge reverted. Contacts restored');
      if (onChanged) onChanged();
      setReverting(null);
    } catch {
      toast.error('Revert failed');
      setReverting(null);
    }
  };

  if (!open || !results) return null;

  const pendingCount = reviewPairs.filter(p => p.status === 'pending').length;
  const autoDetails = results.auto_merged_details || [];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="dup-results-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg">Duplicate Scan Results</DialogTitle>
        </DialogHeader>

        {/* Summary */}
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => results.auto_merged > 0 && setShowAutoMerged(!showAutoMerged)}
            className={`bg-green-50 dark:bg-green-950/30 rounded-lg p-3 text-center transition-all ${results.auto_merged > 0 ? 'hover:ring-2 hover:ring-green-300 cursor-pointer' : ''}`}
            data-testid="auto-merged-summary"
          >
            <p className="text-2xl font-bold text-green-600">{results.auto_merged}</p>
            <p className="text-[11px] text-green-600/80">Auto-merged</p>
            {results.auto_merged > 0 && (
              <p className="text-[9px] text-green-500 mt-0.5">{showAutoMerged ? 'Hide details' : 'Tap to view'}</p>
            )}
          </button>
          <div className="bg-amber-50 dark:bg-amber-950/30 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-amber-600">{reviewPairs.length}</p>
            <p className="text-[11px] text-amber-600/80">Need review</p>
          </div>
        </div>

        <p className="text-xs text-muted-foreground">{results.total_scanned} contacts scanned</p>

        {/* Auto-merged drill-down */}
        {showAutoMerged && autoDetails.length > 0 && (
          <div className="space-y-2 animate-slide-up">
            <p className="text-sm font-semibold">Auto-Merged Contacts</p>
            <div className="space-y-2 max-h-[30vh] overflow-y-auto">
              {autoDetails.map((detail) => (
                <div
                  key={detail.merge_id}
                  className={`rounded-lg border p-3 ${detail.reverted ? 'bg-secondary/50 opacity-60' : 'border-green-200 bg-green-50/50 dark:bg-green-950/20'}`}
                  data-testid={`auto-merged-${detail.merge_id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{detail.keep_name}</p>
                      <p className="text-[10px] text-muted-foreground">
                        merged with: {detail.removed_names.join(', ')}
                      </p>
                      <Badge variant="outline" className="text-[9px] mt-1">{Math.round(detail.confidence * 100)}% match</Badge>
                    </div>
                    {detail.reverted ? (
                      <Badge variant="outline" className="text-[10px] text-muted-foreground ml-2">Reverted</Badge>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => revertAutoMerge(detail)}
                        disabled={reverting === detail.merge_id}
                        className="h-7 rounded-full text-[10px] border-red-200 text-red-600 hover:bg-red-50 ml-2"
                        data-testid={`revert-merge-${detail.merge_id}`}
                      >
                        {reverting === detail.merge_id ? <Loader2 size={12} className="animate-spin" /> : 'Revert'}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Review pairs */}
        {reviewPairs.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold">Potential Duplicates for Review</p>
              {pendingCount > 0 && <Badge className="bg-amber-100 text-amber-700 text-[10px]">{pendingCount} pending</Badge>}
            </div>
            <div className="space-y-2 max-h-[45vh] overflow-y-auto">
              {reviewPairs.map((pair) => (
                <div
                  key={pair._idx}
                  className={`rounded-lg border p-3 transition-colors ${
                    pair.status === 'merged' ? 'bg-green-50 dark:bg-green-950/20 border-green-200' :
                    pair.status === 'skipped' ? 'bg-secondary/50 border-border/50 opacity-60' :
                    'border-border'
                  }`}
                  data-testid={`review-pair-${pair._idx}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <Badge variant="outline" className="text-[10px]">
                      {Math.round(pair.confidence * 100)}% match
                    </Badge>
                    {pair.status === 'merged' && <Badge className="bg-green-500 text-white text-[10px]">Merged</Badge>}
                    {pair.status === 'skipped' && <Badge variant="outline" className="text-[10px] text-muted-foreground">Skipped</Badge>}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-sm font-medium truncate">{pair.contact_a.first_name} {pair.contact_a.last_name || ''}</p>
                      <p className="text-[10px] text-muted-foreground truncate">
                        {pair.contact_a.phones?.[0]?.number || pair.contact_a.emails?.[0]?.email || '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium truncate">{pair.contact_b.first_name} {pair.contact_b.last_name || ''}</p>
                      <p className="text-[10px] text-muted-foreground truncate">
                        {pair.contact_b.phones?.[0]?.number || pair.contact_b.emails?.[0]?.email || '-'}
                      </p>
                    </div>
                  </div>
                  {pair.status === 'pending' && (
                    <div className="flex gap-2 mt-2 pt-2 border-t border-border/30">
                      <Button
                        size="sm"
                        onClick={() => mergePair(pair._idx)}
                        disabled={merging === pair._idx}
                        className="flex-1 h-7 rounded-full text-[11px] bg-sky-500 hover:bg-sky-400 text-white"
                        data-testid={`review-merge-${pair._idx}`}
                      >
                        <GitMerge size={12} className="mr-1" /> {merging === pair._idx ? 'Merging...' : 'Merge'}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => skipPair(pair._idx)}
                        className="flex-1 h-7 rounded-full text-[11px]"
                        data-testid={`review-skip-${pair._idx}`}
                      >
                        <SkipForward size={12} className="mr-1" /> Skip
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {reviewPairs.length === 0 && results.auto_merged === 0 && (
          <div className="text-center py-6">
            <Check size={32} className="mx-auto text-green-500 mb-2" />
            <p className="text-sm font-medium">No duplicates found</p>
            <p className="text-xs text-muted-foreground">All your contacts are unique</p>
          </div>
        )}

        <div className="flex justify-end pt-2">
          <Button size="sm" onClick={onClose} className="rounded-full text-xs" data-testid="dup-results-done-btn">
            Done
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ── Trash (Recently Deleted) Dialog ── */
export function TrashDialog({ open, onClose }) {
  const [deleted, setDeleted] = useState([]);
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState(null);

  useEffect(() => {
    if (open) {
      setLoading(true);
      contactsAPI.listDeleted()
        .then(({ data }) => setDeleted(data || []))
        .catch(() => toast.error('Failed to load deleted contacts'))
        .finally(() => setLoading(false));
    }
  }, [open]);

  const restore = async (id, name) => {
    setRestoring(id);
    try {
      await contactsAPI.restoreDeleted(id);
      setDeleted(prev => prev.filter(c => c.id !== id));
      toast.success(`"${name}" restored`);
    } catch { toast.error('Restore failed'); }
    setRestoring(null);
  };

  const permDelete = async (id, name) => {
    if (!window.confirm(`Permanently delete "${name}"? This cannot be undone.`)) return;
    try {
      await contactsAPI.permanentlyDelete(id);
      setDeleted(prev => prev.filter(c => c.id !== id));
      toast.success('Permanently deleted');
    } catch { toast.error('Delete failed'); }
  };

  const daysAgo = (dateStr) => {
    try {
      const d = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
      return d === 0 ? 'Today' : d === 1 ? 'Yesterday' : `${d} days ago`;
    } catch { return ''; }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md max-h-[80vh] overflow-y-auto overflow-x-hidden" data-testid="trash-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg">Recently Deleted Contacts</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground">Contacts deleted in the last 30 days can be restored.</p>
        <div className="space-y-1 mt-2">
          {loading && <p className="text-sm text-center py-6 text-muted-foreground">Loading...</p>}
          {!loading && deleted.length === 0 && (
            <div className="text-center py-8">
              <Trash2 size={28} className="mx-auto text-muted-foreground/40 mb-2" />
              <p className="text-sm text-muted-foreground">No recently deleted contacts</p>
            </div>
          )}
          {deleted.map(c => {
            const name = [c.first_name, c.last_name].filter(Boolean).join(' ') || c.work?.company || 'Unknown';
            return (
              <div key={c.id} className="flex items-center justify-between py-2 px-2 rounded-lg hover:bg-secondary transition-colors" data-testid={`trash-item-${c.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{name}</p>
                  <p className="text-[10px] text-muted-foreground">{daysAgo(c.deleted_at)}</p>
                </div>
                <div className="flex gap-1 shrink-0 ml-2">
                  <button
                    onClick={() => restore(c.id, name)}
                    disabled={restoring === c.id}
                    className="px-2.5 py-1 rounded-full text-[11px] bg-green-500/10 text-green-600 hover:bg-green-500/20 transition-colors disabled:opacity-50"
                    data-testid={`trash-restore-${c.id}`}
                  >
                    {restoring === c.id ? '...' : 'Restore'}
                  </button>
                  <button
                    onClick={() => permDelete(c.id, name)}
                    className="px-2.5 py-1 rounded-full text-[11px] bg-red-500/10 text-red-600 hover:bg-red-500/20 transition-colors"
                    data-testid={`trash-perm-delete-${c.id}`}
                  >
                    Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ── Enrich Options Modal ── */
export function EnrichOptionsModal({ open, onClose, selectedCount, enriching, enrichProgress, onRun }) {
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !enriching) onClose(); }}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="enrich-options-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg flex items-center gap-2">
            <Sparkles size={18} className="text-nav-blue" /> Enrich Contact Records from Public Data
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div>
            <p className="text-sm font-semibold mb-2">Data to enrich</p>
            <div className="space-y-2">
              <label className="flex items-center gap-2 p-2.5 rounded-lg bg-secondary border border-border cursor-default">
                <Checkbox checked={true} disabled className="pointer-events-none" />
                <div className="flex-1">
                  <span className="text-sm font-medium">City, region & country</span>
                  <p className="text-[10px] text-muted-foreground">Phone area-code + your home region + one public web search per contact</p>
                </div>
                <CheckCircle size={14} className="text-green-500" />
              </label>
              <div className="flex items-center gap-2 p-2.5 rounded-lg bg-muted/50 border border-border opacity-50">
                <Checkbox disabled className="pointer-events-none" />
                <span className="text-sm text-muted-foreground">Image</span>
                <Badge variant="outline" className="text-[9px] ml-auto">Coming soon</Badge>
              </div>
              <div className="flex items-center gap-2 p-2.5 rounded-lg bg-muted/50 border border-border opacity-50">
                <Checkbox disabled className="pointer-events-none" />
                <span className="text-sm text-muted-foreground">Professional info</span>
                <Badge variant="outline" className="text-[9px] ml-auto">Coming soon</Badge>
              </div>
              <div className="flex items-center gap-2 p-2.5 rounded-lg bg-muted/50 border border-border opacity-50">
                <Checkbox disabled className="pointer-events-none" />
                <span className="text-sm text-muted-foreground">Social media profiles</span>
                <Badge variant="outline" className="text-[9px] ml-auto">Coming soon</Badge>
              </div>
            </div>
          </div>

          <p className="text-[11px] text-muted-foreground bg-secondary p-2.5 rounded-lg leading-relaxed" data-testid="enrich-notice">
            PeopleiKnow uses publicly available information for location research. No private or paid data sources are used.
          </p>
          <p className="text-xs text-muted-foreground leading-relaxed" data-testid="enrich-location-disclosure">
            {LOCATION_ENRICHMENT_DISCLOSURE}
          </p>

          {enriching && (
            <div className="flex items-center gap-2 text-sm text-nav-blue" data-testid="enrich-progress">
              <Loader2 size={14} className="animate-spin" />
              <span>{enrichProgress}</span>
            </div>
          )}

          <div className="flex gap-2 pt-1">
            <Button onClick={onClose} variant="outline" className="flex-1 rounded-full h-9 text-xs" disabled={enriching} data-testid="enrich-cancel-btn">
              Cancel
            </Button>
            <Button onClick={onRun} className="flex-1 rounded-full h-9 text-xs bg-nav-blue hover:bg-nav-blue/80" disabled={enriching} data-testid="enrich-run-btn">
              {enriching ? 'Enriching...' : `Run enrichment (${selectedCount} contact${selectedCount !== 1 ? 's' : ''})`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ── Enrich Results Modal ── */
export function EnrichResultsModal({ open, results, onClose, onAccept, onSkip }) {
  if (!results) return null;
  const { auto_updated_count, auto_updated, needs_review_count, needs_review, skipped_count } = results;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="enrich-results-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg flex items-center gap-2">
            <Sparkles size={18} className="text-nav-blue" /> Enrichment Results
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          {/* Summary */}
          <div className="grid grid-cols-3 gap-2" data-testid="enrich-summary">
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
              <p className="text-xl font-bold text-green-600">{auto_updated_count}</p>
              <p className="text-[10px] text-green-700">Auto-updated</p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
              <p className="text-xl font-bold text-amber-600">{needs_review_count}</p>
              <p className="text-[10px] text-amber-700">Needs review</p>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-center">
              <p className="text-xl font-bold text-slate-500">{skipped_count}</p>
              <p className="text-[10px] text-slate-600">No match</p>
            </div>
          </div>

          {/* Auto-updated list */}
          {auto_updated && auto_updated.length > 0 && (
            <div>
              <p className="text-sm font-semibold mb-1.5 flex items-center gap-1.5">
                <CheckCircle size={14} className="text-green-500" /> Auto-updated ({auto_updated.length})
              </p>
              <div className="space-y-1.5 max-h-40 overflow-y-auto">
                {auto_updated.map((r) => {
                  const loc = [r.city, r.state, r.country].filter(Boolean).join(', ');
                  const band = r.confidence_band || 'high';
                  return (
                    <div key={r.contact_id} className="flex items-center justify-between bg-green-50 border border-green-100 rounded-lg px-3 py-2" data-testid={`enrich-auto-${r.contact_id}`}>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate">{r.name}</p>
                        <p className="text-[10px] text-muted-foreground truncate">
                          {r.existing_city ? `${r.existing_city} → ` : ''}{loc}
                        </p>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0 ml-2">
                        {r.source && <span className="text-[9px] text-green-500 bg-green-100 px-1 py-0.5 rounded" title={r.reasoning}>{r.source}</span>}
                        <span className="text-[10px] text-green-700 font-semibold uppercase tracking-wider" data-testid={`enrich-auto-band-${r.contact_id}`}>{band}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Needs review list */}
          {needs_review && needs_review.length > 0 && (
            <div>
              <p className="text-sm font-semibold mb-1.5 flex items-center gap-1.5">
                <AlertTriangle size={14} className="text-amber-500" /> Needs review ({needs_review.length})
              </p>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {needs_review.map((r) => {
                  const loc = [r.city, r.state, r.country].filter(Boolean).join(', ');
                  const band = r.confidence_band || 'medium';
                  const bandClasses = band === 'medium'
                    ? 'text-amber-700 bg-amber-100'
                    : 'text-slate-700 bg-slate-100';
                  return (
                    <div key={r.contact_id} className="bg-amber-50 border border-amber-100 rounded-lg p-3" data-testid={`enrich-review-${r.contact_id}`}>
                      <div className="flex items-start justify-between mb-1.5">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium">{r.name}</p>
                          <p className="text-[10px] text-muted-foreground">
                            Current: {r.existing_city || 'None'}
                          </p>
                        </div>
                        <span
                          className={`text-[10px] font-semibold ${bandClasses} px-1.5 py-0.5 rounded shrink-0 ml-2 uppercase tracking-wider`}
                          data-testid={`enrich-review-band-${r.contact_id}`}
                        >
                          {band} confidence
                        </span>
                      </div>
                      <p className="text-sm text-nav-blue font-medium mb-1">
                        Suggested: {loc}
                      </p>
                      {r.reasoning && (
                        <p className="text-[10px] text-muted-foreground italic mb-2" title={r.reasoning}>
                          {r.source && <span className="font-medium not-italic">{r.source}: </span>}
                          {r.reasoning.length > 90 ? `${r.reasoning.slice(0, 90)}…` : r.reasoning}
                        </p>
                      )}
                      <div className="flex gap-2">
                        <Button size="sm" className="h-7 text-xs rounded-full bg-green-500 hover:bg-green-600 flex-1" onClick={() => onAccept(r.contact_id, r.city, r.state, r.country)} data-testid={`enrich-accept-${r.contact_id}`}>
                          <Check size={12} className="mr-1" /> Accept
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 text-xs rounded-full flex-1" onClick={() => onSkip(r.contact_id)} data-testid={`enrich-skip-${r.contact_id}`}>
                          <SkipForward size={12} className="mr-1" /> Skip
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Done button */}
          <Button onClick={onClose} className="w-full rounded-full h-9 text-xs" data-testid="enrich-done-btn">
            Done
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ── Enrich All Un-enriched Preview Modal (Option C, session 126m) ──
 * Shown when the user clicks the "Enrich all un-enriched contacts"
 * button. Hits GET /contacts/enrich/preview for the live counts and
 * cost estimate, then offers a single Run button that POSTs to
 * /contacts/enrich/all-unenriched. The copy is exact per user spec:
 *
 *   "Enriching X contacts will take ~Y minutes. We'll notify you
 *    once done. This is free for you. It's worth ~$Z in AI token
 *    costs."   (Z = 0.0125 × X, computed server-side.)
 */
export function EnrichBulkPreviewModal({ open, onClose, onConfirmed }) {
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setPreview(null);
    setLoading(true);
    contactsAPI.enrichPreview()
      .then(({ data }) => setPreview(data))
      .catch((err) => setError(err?.response?.data?.detail || 'Could not load preview'))
      .finally(() => setLoading(false));
  }, [open]);

  const handleRun = async () => {
    if (!preview || preview.eligible === 0) return;
    setRunning(true);
    try {
      const { data } = await contactsAPI.enrichAllUnenriched();
      toast.success(
        `Enriching ${preview.eligible} contact${preview.eligible !== 1 ? 's' : ''} in the background. ` +
        `We'll notify you when it's ready — keep working in the meantime.`,
        { duration: 6000 },
      );
      onConfirmed?.(data?.job_id);
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not start enrichment';
      toast.error(msg);
    } finally {
      setRunning(false);
    }
  };

  // Friendly cost — server already rounds, but be defensive.
  const cost = preview?.estimated_cost_usd != null
    ? `$${preview.estimated_cost_usd.toFixed(2)}`
    : '';

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !running) onClose(); }}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="enrich-bulk-preview-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg flex items-center gap-2">
            <Sparkles size={18} className="text-nav-blue" /> Enrich all un-enriched contacts
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-6" data-testid="enrich-bulk-loading">
              <Loader2 size={14} className="animate-spin" /> Counting your contacts…
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700" data-testid="enrich-bulk-error">
              {error}
            </div>
          )}

          {preview && !loading && (
            <>
              <div className="grid grid-cols-2 gap-2" data-testid="enrich-bulk-stats">
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-emerald-700" data-testid="enrich-bulk-eligible">{preview.eligible}</p>
                  <p className="text-[10px] text-emerald-700 uppercase tracking-wide">Un-enriched · will run</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-slate-600" data-testid="enrich-bulk-already">
                    {preview.already_enriched + preview.already_synced_via_peopleiknow}
                  </p>
                  <p className="text-[10px] text-slate-600 uppercase tracking-wide">Already enriched / synced</p>
                </div>
              </div>

              {preview.eligible > 0 ? (
                <div className="bg-secondary border border-border rounded-lg p-3 leading-relaxed" data-testid="enrich-bulk-copy">
                  <p className="text-sm">
                    Enriching <span className="font-semibold">{preview.eligible}</span>{' '}
                    contact{preview.eligible === 1 ? '' : 's'} will take{' '}
                    <span className="font-semibold">~{preview.estimated_minutes}</span>{' '}
                    minute{preview.estimated_minutes === 1 ? '' : 's'}.
                    We&apos;ll notify you once done.
                  </p>
                  <p className="text-sm mt-2">
                    This is <span className="font-semibold text-emerald-700">free for you</span>.
                    It&apos;s worth <span className="font-semibold" data-testid="enrich-bulk-cost">~{cost}</span> in AI token costs.
                  </p>
                </div>
              ) : (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-800" data-testid="enrich-bulk-none-eligible">
                  Nothing to do — every contact is either already enriched
                  or auto-synced via PeopleiKnow.
                </div>
              )}

              {preview.already_synced_via_peopleiknow > 0 && (
                <p className="text-[11px] text-muted-foreground" data-testid="enrich-bulk-skip-note">
                  {preview.already_synced_via_peopleiknow} contact{preview.already_synced_via_peopleiknow === 1 ? ' is' : 's are'} auto-synced via PeopleiKnow and will be skipped.
                </p>
              )}
            </>
          )}

          <p className="text-xs text-muted-foreground leading-relaxed" data-testid="enrich-bulk-location-disclosure">
            {LOCATION_ENRICHMENT_DISCLOSURE}
          </p>
          <div className="flex gap-2 pt-1">
            <Button
              onClick={onClose}
              variant="outline"
              className="flex-1 rounded-full h-9 text-xs"
              disabled={running}
              data-testid="enrich-bulk-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleRun}
              className="flex-1 rounded-full h-9 text-xs bg-nav-blue hover:bg-nav-blue/80"
              disabled={loading || running || !preview || preview.eligible === 0}
              data-testid="enrich-bulk-confirm-btn"
            >
              {running ? <><Loader2 size={12} className="mr-1 animate-spin" /> Starting…</>
                : preview?.eligible
                  ? `Run enrichment (${preview.eligible})`
                  : 'Run enrichment'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


/* ── Unified Pipeline Results Modal (session 126q) ──
 * Shown when a chained-pipeline job (`pipeline_mode === true` AND
 * `results.per_stage` present) completes. Renders one card per stage
 * with headline numbers + a "Review" CTA where applicable:
 *
 *   • Phones    — count of contacts whose number was normalized to
 *                  E.164. Auto-applied; no review needed.
 *   • Dedup     — count of pairs auto-merged at ≥0.99 confidence.
 *                  Merged duplicate is soft-deleted; surviving
 *                  contact carries `merged_from_ids`. The CTA opens
 *                  a fresh scan so the user can see what's left.
 *   • Locations — auto-updated + needs-review counts. CTA delegates
 *                  to the existing EnrichResultsModal review queue
 *                  (the session-126m component).
 */
export function PipelineResultsModal({ open, job, onClose, onReviewLocations, onReviewMerges, onReviewNames }) {
  if (!job) return null;
  const perStage = job.results?.per_stage || {};
  const phones = perStage.phones || {};
  const dedup = perStage.dedup || {};
  const names = perStage.names || {};
  const locations = perStage.locations || {};

  const phoneCount = phones.updated || 0;
  const mergeCount = dedup.auto_merged || 0;
  const nameAutoCount = names.auto_applied_count || 0;
  const nameReviewCount = names.needs_review_count || 0;
  const locCount = locations.auto_updated_count || 0;
  const reviewCount = locations.needs_review_count || 0;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="pipeline-results-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg flex items-center gap-2">
            <CheckCircle size={18} className="text-emerald-600" />
            Enrichment complete
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 pt-2">
          {/* Top-line summary */}
          <p className="text-xs text-muted-foreground leading-relaxed" data-testid="pipeline-summary">
            <span className="font-semibold text-foreground">{phoneCount}</span> phones normalized &middot;{' '}
            <span className="font-semibold text-foreground">{mergeCount}</span> duplicates merged &middot;{' '}
            <span className="font-semibold text-foreground">{nameAutoCount}</span> names cleaned &middot;{' '}
            <span className="font-semibold text-foreground">{locCount}</span> locations found
            {(reviewCount + nameReviewCount) > 0 && (
              <>{' '}&middot; <span className="font-semibold text-amber-600">{reviewCount + nameReviewCount} need review</span></>
            )}.
          </p>

          {/* Phones stage */}
          {('scanned' in phones) && (
            <div className="border border-border rounded-lg p-3 bg-card" data-testid="pipeline-stage-phones">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-semibold flex items-center gap-1.5">
                  <CheckCircle size={12} className="text-emerald-600" />
                  Standardize phone numbers
                </span>
                <span className="text-[10px] text-muted-foreground">{phones.scanned || 0} scanned</span>
              </div>
              <p className="text-[11px] text-muted-foreground">
                {phoneCount > 0
                  ? `${phoneCount} phone number${phoneCount === 1 ? '' : 's'} normalized to E.164 format.`
                  : 'No phone numbers needed normalization.'}
              </p>
            </div>
          )}

          {/* Dedup stage */}
          {('scanned' in dedup) && (
            <div className="border border-border rounded-lg p-3 bg-card" data-testid="pipeline-stage-dedup">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-semibold flex items-center gap-1.5">
                  <CheckCircle size={12} className="text-emerald-600" />
                  Deduplicate contacts
                </span>
                <span className="text-[10px] text-muted-foreground">{dedup.scanned || 0} scanned</span>
              </div>
              <p className="text-[11px] text-muted-foreground">
                {mergeCount > 0
                  ? `${mergeCount} duplicate pair${mergeCount === 1 ? '' : 's'} auto-merged at high confidence.`
                  : 'No duplicates detected at the auto-merge threshold.'}
              </p>
              {onReviewMerges && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onReviewMerges}
                  className="mt-2 h-7 text-[11px] rounded-full"
                  data-testid="pipeline-review-merges-btn"
                >
                  Run a fresh duplicate scan
                </Button>
              )}
            </div>
          )}

          {/* Names stage (session 128) */}
          {('scanned' in names || 'flagged' in names) && (
            <div className="border border-border rounded-lg p-3 bg-card" data-testid="pipeline-stage-names">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-semibold flex items-center gap-1.5">
                  <CheckCircle size={12} className="text-emerald-600" />
                  Clean up names
                </span>
                <span className="text-[10px] text-muted-foreground">{names.scanned || 0} scanned</span>
              </div>
              <p className="text-[11px] text-muted-foreground">
                {nameAutoCount > 0 && <>{nameAutoCount} name{nameAutoCount === 1 ? '' : 's'} auto-cleaned (embedded notes moved to comments).</>}
                {nameAutoCount > 0 && nameReviewCount > 0 && ' '}
                {nameReviewCount > 0 && (
                  <span className="text-amber-600 font-medium">
                    {nameReviewCount} need your confirmation.
                  </span>
                )}
                {nameAutoCount === 0 && nameReviewCount === 0 && 'No embedded notes found in name fields.'}
              </p>
              {(nameAutoCount > 0 || nameReviewCount > 0) && onReviewNames && (
                <Button
                  size="sm"
                  onClick={onReviewNames}
                  className="mt-2 h-7 text-[11px] rounded-full bg-nav-blue hover:bg-nav-blue/80"
                  data-testid="pipeline-review-names-btn"
                >
                  {nameReviewCount > 0
                    ? `Review ${nameReviewCount} name${nameReviewCount === 1 ? '' : 's'}`
                    : 'See cleaned names'}
                </Button>
              )}
            </div>
          )}

          {/* Locations stage */}
          {('auto_updated_count' in locations || 'needs_review_count' in locations) && (
            <div className="border border-border rounded-lg p-3 bg-card" data-testid="pipeline-stage-locations">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-semibold flex items-center gap-1.5">
                  <CheckCircle size={12} className="text-emerald-600" />
                  Auto-populate home cities
                </span>
                <span className="text-[10px] text-muted-foreground">
                  {locations.total || (locCount + reviewCount + (locations.skipped_count || 0))} scanned
                </span>
              </div>
              <p className="text-[11px] text-muted-foreground">
                {locCount > 0 && <>{locCount} location{locCount === 1 ? '' : 's'} auto-updated.</>}
                {locCount > 0 && reviewCount > 0 && ' '}
                {reviewCount > 0 && (
                  <span className="text-amber-600 font-medium">
                    {reviewCount} need your input.
                  </span>
                )}
                {locCount === 0 && reviewCount === 0 && 'No new locations to suggest.'}
              </p>
              {reviewCount > 0 && onReviewLocations && (
                <Button
                  size="sm"
                  onClick={onReviewLocations}
                  className="mt-2 h-7 text-[11px] rounded-full bg-nav-blue hover:bg-nav-blue/80"
                  data-testid="pipeline-review-locations-btn"
                >
                  Review {reviewCount} location{reviewCount === 1 ? '' : 's'}
                </Button>
              )}
            </div>
          )}

          <Button
            onClick={onClose}
            className="w-full rounded-full h-9 text-xs mt-2"
            data-testid="pipeline-done-btn"
          >
            Done
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


/* ── Name Hygiene Review Modal (session 128) ──
 * Outcome of a name-cleanup scan:
 *   • auto-applied (high confidence) — listed with one-tap Undo
 *   • needs review (medium/low)     — before → after with Apply / Skip
 * Descriptors route to Connection comments (searchable) or, for
 * professions, into the empty Job title field.
 */
export function NameHygieneReviewModal({ open, results, onClose, onApply, onSkip, onRevert }) {
  if (!results) return null;
  const auto = results.auto_applied || [];
  const review = results.needs_review || [];
  const chip = (t) => ({
    relationship: 'bg-rose-100 text-rose-700',
    profession: 'bg-sky-100 text-sky-700',
    place: 'bg-emerald-100 text-emerald-700',
    event: 'bg-amber-100 text-amber-700',
  }[t] || 'bg-slate-100 text-slate-600');

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="name-hygiene-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg flex items-center gap-2">
            <Sparkles size={18} className="text-nav-blue" /> Name cleanup
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-1">
          <p className="text-xs text-muted-foreground" data-testid="name-hygiene-summary">
            {results.flagged || 0} of {results.scanned || 0} scanned name{(results.scanned || 0) === 1 ? '' : 's'} looked
            like they contain embedded notes.
          </p>

          {auto.length > 0 && (
            <div className="space-y-2">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                Auto-cleaned ({auto.length})
              </p>
              {auto.map((item) => (
                <div
                  key={item.contact_id}
                  className="border border-emerald-200 rounded-lg p-2.5 bg-emerald-50/50"
                  data-testid={`name-auto-${item.contact_id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-[11px] text-muted-foreground line-through truncate">{item.name_before}</p>
                      <p className="text-sm font-medium truncate">{item.name_after}</p>
                      <p className="text-[11px] mt-1 flex items-center gap-1.5 min-w-0">
                        <span className={`px-1.5 py-0.5 rounded shrink-0 ${chip(item.descriptor_type)}`}>{item.descriptor_type}</span>
                        <span className="text-muted-foreground truncate">
                          &ldquo;{item.descriptor}&rdquo; → {item.descriptor_target === 'work.title' ? 'Job title' : 'Connection comments'}
                        </span>
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onRevert(item.contact_id)}
                      className="h-7 text-[11px] shrink-0 text-muted-foreground"
                      data-testid={`name-undo-${item.contact_id}`}
                    >
                      Undo
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {review.length > 0 && (
            <div className="space-y-2">
              <p className="text-[10px] uppercase tracking-wider text-amber-600 font-semibold">
                Needs your confirmation ({review.length})
              </p>
              {review.map((item) => (
                <div
                  key={item.contact_id}
                  className="border border-border rounded-lg p-2.5 bg-card"
                  data-testid={`name-review-${item.contact_id}`}
                >
                  <p className="text-[11px] text-muted-foreground line-through truncate">{item.name_before}</p>
                  <p className="text-sm font-medium truncate">{item.name_after}</p>
                  <p className="text-[11px] mt-1 flex items-center gap-1.5 min-w-0">
                    <span className={`px-1.5 py-0.5 rounded shrink-0 ${chip(item.descriptor_type)}`}>{item.descriptor_type}</span>
                    <span className="text-muted-foreground truncate">
                      &ldquo;{item.descriptor}&rdquo; → {item.descriptor_type === 'profession' ? 'Job title' : 'Connection comments'}
                    </span>
                  </p>
                  {item.reasoning && (
                    <p className="text-[10px] text-muted-foreground/70 mt-1 leading-snug">{item.reasoning}</p>
                  )}
                  <div className="flex gap-2 mt-2">
                    <Button
                      size="sm"
                      onClick={() => onApply(item)}
                      className="h-7 text-[11px] rounded-full bg-nav-blue hover:bg-nav-blue/80"
                      data-testid={`name-apply-${item.contact_id}`}
                    >
                      <Check size={11} className="mr-1" /> Apply
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onSkip(item)}
                      className="h-7 text-[11px] rounded-full"
                      data-testid={`name-skip-${item.contact_id}`}
                    >
                      <SkipForward size={11} className="mr-1" /> Skip
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {auto.length === 0 && review.length === 0 && (
            <p className="text-sm text-muted-foreground py-2" data-testid="name-hygiene-empty">
              All names look clean — nothing to fix.
            </p>
          )}

          {(results.suppressed_count || 0) > 0 && (
            <p className="text-[11px] text-muted-foreground" data-testid="name-hygiene-suppressed-note">
              {results.suppressed_count} suggestion{results.suppressed_count === 1 ? '' : 's'} hidden
              because you&apos;ve skipped similar descriptors before.
            </p>
          )}

          <Button
            onClick={onClose}
            className="w-full rounded-full h-9 text-xs"
            data-testid="name-hygiene-done-btn"
          >
            Done
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
